<template>
  <div class="rv-finance-report">
    <!-- Cut Off By -->
    <div class="rv-finance-report__form-row">
      <label class="rv-finance-report__label">Cut Off By</label>
      <div class="rv-finance-report__field">
        <v-select
          v-model="financeReportData.cutOffBy"
          :error-messages="financeReportError.cutOffBy"
          :items="cutOffItems"
          outlined
          placeholder="Select cut off"
          dense
          hide-details="auto"
          class="rv-finance-report__select"
        ></v-select>
      </div>
    </div>

    <!-- Date Range -->
    <div class="rv-finance-report__form-row">
      <label class="rv-finance-report__label">Date Range</label>
      <div class="rv-finance-report__field">
        <v-menu
          ref="menu2"
          v-model="menu2"
          :close-on-content-click="false"
          :return-value.sync="date"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <div
              class="rv-datepicker__trigger"
              :class="{ 'rv-datepicker__trigger--error': financeReportError.dateRange }"
              v-bind="attrs"
              v-on="on"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="rv-datepicker__icon">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
              </svg>
              <span v-if="searchRptDate" class="rv-datepicker__text">{{ searchRptDate }}</span>
              <span v-else class="rv-datepicker__placeholder">Select date range...</span>
            </div>
          </template>
          <v-date-picker color="var(--app-accent)"
            range
            v-model="financeReportData.searchRptDateRange"
            no-title
            scrollable
          >
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="menu2 = false"> Cancel </v-btn>
            <v-btn text color="primary" @click="$refs.menu2.save(date)">
              OK
            </v-btn>
          </v-date-picker>
        </v-menu>
        <div v-if="financeReportError.dateRange" class="rv-finance-report__error">{{ financeReportError.dateRange }}</div>
      </div>
    </div>

    <!-- Generate Button -->
    <div class="rv-finance-report__actions">
      <button class="rv-finance-report__generate-btn" @click="generateFinanceReport()">
        <v-icon small color="#fff" class="mr-2">mdi-file-download-outline</v-icon>
        Generate Finance Report
      </button>
    </div>
  </div>
</template>

<script>
export default {
  computed: {
    searchRptDate() {
      if (this.financeReportData.searchRptDateRange) {
        return this.financeReportData.searchRptDateRange.join(" - ");
      }

      return null;
    },
  },
  emits: ["generateFinanceReport"],
  props: {
    financeReportData: Object,
    financeReportError: Object,
  },
  data: () => ({
    date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substr(0, 10),
    menu2: false,
    cutOffItems: ["Payment Received Date"],
  }),
  created() {
    //
  },
  methods: {
    generateFinanceReport() {
      this.$emit("generateFinanceReport", this.financeReportData);
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .rv-finance-report__label {
  color: var(--app-text);
}

.dark-mode .rv-finance-report__error {
  color: var(--app-danger);
}

.dark-mode .rv-finance-report__select >>> fieldset {
  border-color: var(--app-border) !important;
}

.dark-mode .rv-finance-report__select:hover >>> fieldset {
  border-color: var(--app-border-strong) !important;
}

.dark-mode .rv-finance-report__select >>> .v-select__selection {
  color: var(--app-text) !important;
}

.dark-mode .rv-finance-report__select >>> input::placeholder {
  color: var(--app-text-faint) !important;
}

.dark-mode .rv-datepicker__trigger {
  border: 1px solid var(--app-border-strong);
  background: var(--app-card);
}

.dark-mode .rv-datepicker__trigger:hover {
  border-color: var(--app-border-strong);
}

.dark-mode .rv-datepicker__icon {
  color: var(--app-text-faint);
}

.dark-mode .rv-datepicker__text {
  color: var(--app-text);
}

.dark-mode .rv-datepicker__placeholder {
  color: var(--app-text-faint);
}

/* >>> end auto-dark <<< */

/* ============================================
   PAGE LAYOUT
   ============================================ */
.rv-finance-report {
  font-family: 'Lato', sans-serif;
  padding: 24px 28px;
  max-width: 640px;
}

/* ============================================
   FORM ROWS
   ============================================ */
.rv-finance-report__form-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 18px;
  gap: 16px;
}

.rv-finance-report__label {
  flex: 0 0 140px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #3a3f47;
  line-height: 1.6;
  padding-top: 10px;
}

.dark-mode .rv-finance-report__label {
  color: var(--app-text-muted);
}

.rv-finance-report__field {
  flex: 1;
  min-width: 0;
}

.rv-finance-report__error {
  color: #dc2626;
  font-size: 0.75rem;
  margin-top: 4px;
  padding-left: 4px;
}

/* ============================================
   SELECT STYLING
   ============================================ */
.rv-finance-report__select >>> fieldset {
  border-color: #e2e8f0 !important;
  border-radius: 10px !important;
}

.rv-finance-report__select >>> .v-input__slot {
  min-height: 42px !important;
}

.rv-finance-report__select:hover >>> fieldset {
  border-color: #94a3b8 !important;
}

.rv-finance-report__select.v-input--is-focused >>> fieldset {
  border-color: #0ea5e9 !important;
}

.rv-finance-report__select >>> .v-select__selection {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.85rem !important;
  font-weight: 600 !important;
  color: #1e293b !important;
}

.rv-finance-report__select >>> input::placeholder {
  color: #94a3b8 !important;
  font-weight: 500 !important;
}

/* ============================================
   DATEPICKER TRIGGER
   ============================================ */
.rv-datepicker__trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 42px;
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: 'Lato', sans-serif;
  font-size: 0.85rem;
}

.rv-datepicker__trigger:hover {
  border-color: #94a3b8;
}

.rv-datepicker__trigger--error {
  border-color: #ef4444;
}

.rv-datepicker__icon {
  width: 18px;
  height: 18px;
  color: #94a3b8;
  flex-shrink: 0;
}

.rv-datepicker__text {
  color: #1e293b;
  font-weight: 600;
}

.rv-datepicker__placeholder {
  color: #94a3b8;
}

/* ============================================
   BUTTONS
   ============================================ */
.rv-finance-report__actions {
  display: flex;
  justify-content: flex-start;
  padding-top: 8px;
  padding-left: 156px;
}

.rv-finance-report__generate-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  background: linear-gradient(135deg, #0ea5e9, #38bdf8);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-family: 'Lato', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(14, 165, 233, 0.25);
}

.rv-finance-report__generate-btn:hover {
  background: linear-gradient(135deg, #0284c7, #0ea5e9);
  box-shadow: 0 4px 14px rgba(14, 165, 233, 0.35);
  transform: translateY(-1px);
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 768px) {
  .rv-finance-report {
    padding: 16px 14px;
    max-width: 100%;
  }

  .rv-finance-report__form-row {
    flex-direction: column;
    gap: 6px;
  }

  .rv-finance-report__label {
    flex: none;
    padding-top: 0;
  }

  .rv-finance-report__actions {
    padding-left: 0;
  }
}
</style>

<style>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .v-picker__body {
  background: var(--app-card) !important;
}

.dark-mode .v-date-picker-header {
  background: var(--app-card) !important;
}

.dark-mode .v-date-picker-header__value button {
  color: var(--app-text) !important;
}

.dark-mode .v-date-picker-header .v-btn {
  color: var(--app-text-muted) !important;
}

.dark-mode .v-date-picker-table thead th {
  color: var(--app-text-faint) !important;
}

.dark-mode .v-date-picker-table .v-btn {
  color: var(--app-text) !important;
}

.dark-mode .v-date-picker-table .v-btn:hover {
  background: var(--app-accent-soft) !important;
}

.dark-mode .v-picker__actions {
  background: var(--app-card) !important;
  border-top: 1px solid var(--app-border) !important;
}

/* >>> end auto-dark <<< */

/* Date picker popup — Tailwind theme */
.v-picker {
  border-radius: 14px !important;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
  border: none !important;
  overflow: hidden;
  font-family: "Lato", sans-serif !important;
}

.v-picker .v-picker__body { border: none !important; }

/* The unscoped `.v-menu__content …` block that used to sit here is gone.
   It was global — unscoped CSS is injected once and never removed, so a rule
   written for THIS page's dropdown repainted every menu in the app (with
   !important, and with hard-coded light hex) for the rest of the session. That
   is what made the same v-select look different depending on which page you had
   visited. Menus are defined ONCE, in tokens, in src/plugins/theme-glass.css §3
   — panel, list, item radius, hover, active, typography and the state overlay.
   A page that genuinely needs its own menu styling should give that v-menu a
   `content-class` and key off it, the way .bdt-menu and .tb-view-menu do. */
.v-picker__body { background: #fff !important; }

.v-date-picker-header { padding: 12px 16px 8px !important; background: #fff !important; }
.v-date-picker-header__value button { font-family: "Lato", sans-serif !important; font-weight: 700 !important; font-size: 0.95rem !important; color: #1e293b !important; }
.v-date-picker-header .v-btn { color: #64748b !important; }

.v-date-picker-table thead th { font-family: "Lato", sans-serif !important; font-size: 0.72rem !important; font-weight: 600 !important; color: #94a3b8 !important; padding: 8px 0 !important; }
.v-date-picker-table .v-btn { font-family: "Lato", sans-serif !important; font-size: 0.82rem !important; font-weight: 500 !important; color: #334155 !important; }
.v-date-picker-table .v-btn:hover { background: #f0f9ff !important; }
.v-date-picker-table .v-btn--active { background: #0ea5e9 !important; color: #fff !important; font-weight: 700 !important; }
.v-date-picker-table .v-btn--active:hover { background: #0284c7 !important; }
.v-date-picker-table .v-btn--outlined { border-color: #0ea5e9 !important; color: #0ea5e9 !important; }

.v-picker__actions { padding: 8px 16px 14px !important; background: #fff !important; border-top: 1px solid #f1f5f9 !important; }
.v-picker__actions .v-btn { font-family: "Lato", sans-serif !important; font-weight: 600 !important; font-size: 0.82rem !important; text-transform: none !important; border-radius: 8px !important; padding: 0 16px !important; }

.v-date-picker-years li, .v-date-picker-months .v-btn { font-family: "Lato", sans-serif !important; }
</style>
